/**
 * M2 P1: ship definitions. Pure, deterministic, engine-agnostic (no Three.js) so
 * the same specs drive the client now and a server later (P8 multiplayer).
 *
 * A ship = a RigidBody (position/velocity/orientation/mass/inertia) parameterised
 * by a ShipSpec (handling + capacities) plus a mutable ShipState (fuel/hull/
 * shields/energy). The flight controller reads THRUST/damping/turn-rates from the
 * active spec so handling differs per ship type.
 */
import type { Hardpoint, Loadout } from './modules.js';
import type { InertiaTensor } from './rigidbody.js';
import type { FactionId } from './faction.js';
import { vec3 } from './vec3.js';

export interface ShipSpec {
  id: string;
  name: string;
  className: 'light' | 'medium' | 'heavy';
  /** SHIP-EXPANSION: owning faction (optional; absent on the 4 player ships,
   * which resolve to 'guild' via factionOf). Drives faction-scoped spawning. */
  faction?: FactionId;
  // --- mass / inertia (fed into the RigidBody) ---
  massKg: number;
  inertia: InertiaTensor;
  // --- linear handling (sim units = km; velocities km/s) ---
  thrust: number; // force magnitude applied at full throttle/strafe
  linearDamping: number; // flight-assist linear damping (terminal ≈ thrust/mass/damping)
  maxSpeedKmS: number; // soft top speed under assist (HUD + clamp reference)
  // --- rotational handling ---
  /** @deprecated superseded by the sim-core mouseStick virtual-stick model (M4
   * control-feel) — mouse no longer rotates the ship directly. Kept to avoid
   * spec churn; remove in a later cleanup pass. */
  mouseSens: number; // rad per px of mouse delta (legacy direct displacement)
  keyTurnRate: number; // rad/s MAX yaw/pitch rate (reused as the drive maxRate)
  rollRate: number; // rad/s MAX roll rate (reused as the drive maxRate)
  angularDamping: number; // FA-ON rotational damping (released stick decays angVel)
  // FLIGHT-FEEL A1: rate-servo gains (1/s). Higher = snappier chase to target rate.
  pitchYawAccel: number; // servo gain for pitch + yaw axes
  rollAccel: number; // servo gain for the roll axis
  maxAngAccel: number; // rad/s^2 cap; per-axis maxTorque = inertia * maxAngAccel
  // FLIGHT-FEEL A5: throttle "blue zone" — best handling band, as fractions of maxSpeed.
  optimalSpeedLo: number; // lower bound fraction of maxSpeedKmS
  optimalSpeedHi: number; // upper bound fraction of maxSpeedKmS
  // --- capacities ---
  fuelMax: number; // arbitrary units
  hullMax: number;
  shieldMax: number;
  energyMax: number;
  fuelBurnPerSec: number; // drain at full thrust
  energyRegenPerSec: number;
  shieldRegenPerSec: number;
}

/** Mutable per-ship resource state. */
export interface ShipState {
  fuel: number;
  hull: number;
  shield: number;
  energy: number;
}

/** Terminal speed under flight-assist for a spec: (thrust/mass)/linearDamping. */
export const terminalSpeed = (spec: ShipSpec): number =>
  spec.thrust / spec.massKg / spec.linearDamping;

/**
 * FLIGHT-FEEL A5: true when current speed sits in the spec's optimal "blue zone"
 * (a fraction band of maxSpeedKmS). The controller rewards this band with extra
 * turn rate, so there's a mechanical payoff to flying at the right speed (Elite's
 * blue-zone manoeuvrability). Pure, deterministic.
 */
export const inBlueZone = (speedKmS: number, spec: ShipSpec): boolean =>
  speedKmS >= spec.maxSpeedKmS * spec.optimalSpeedLo &&
  speedKmS <= spec.maxSpeedKmS * spec.optimalSpeedHi;

/** Fresh full state for a spec. */
export const freshState = (spec: ShipSpec): ShipState => ({
  fuel: spec.fuelMax,
  hull: spec.hullMax,
  shield: spec.shieldMax,
  energy: spec.energyMax,
});

/**
 * Advance ship resources by dt. Thrusting burns fuel; energy + shield regen when
 * not over-drawn. Pure: returns a new ShipState, clamped to [0, max].
 * @param thrustFrac 0..1 fraction of max thrust currently commanded
 */
const clamp0 = (v: number, max: number): number => Math.max(0, Math.min(max, v));

export const tickResources = (
  spec: ShipSpec,
  s: ShipState,
  dt: number,
  thrustFrac: number,
): ShipState => {
  const clamp = clamp0;
  const fuel = clamp(s.fuel - spec.fuelBurnPerSec * thrustFrac * dt, spec.fuelMax);
  const energy = clamp(s.energy + spec.energyRegenPerSec * dt, spec.energyMax);
  const shield = clamp(s.shield + spec.shieldRegenPerSec * dt, spec.shieldMax);
  return { fuel, energy, shield, hull: clamp(s.hull, spec.hullMax) };
};

// --- Starter fleet: 4 types with distinct mass/inertia/thrust/handling ---

const makeHardpoint = (
  id: string,
  nodeName: string,
  kind: Hardpoint['kind'],
  size: Hardpoint['size'],
  mountType: Hardpoint['mountType'],
  paletteRole: Hardpoint['paletteRole'],
  powerBudget: number,
  position: [number, number, number],
  forward: [number, number, number] = [0, 0, 1],
  up: [number, number, number] = [0, 1, 0],
): Hardpoint => ({
  id,
  nodeName,
  kind,
  size,
  mountType,
  paletteRole,
  powerBudget,
  transform: {
    position: vec3(...position),
    forward: vec3(...forward),
    up: vec3(...up),
  },
});

const makeSkiff = (): ShipSpec => ({
  id: 'skiff',
  name: 'Pallbearer',
  className: 'light',
  massKg: 26_000,
  inertia: { ix: 3.4e5, iy: 3.4e5, iz: 1.7e5 },
  thrust: 210_000,
  linearDamping: 0.46,
  maxSpeedKmS: 16,
  mouseSens: 0.004,
  keyTurnRate: 2.0,
  rollRate: 2.3,
  angularDamping: 8.5,
  pitchYawAccel: 7.0,
  rollAccel: 8.5,
  maxAngAccel: 4.5,
  optimalSpeedLo: 0.42,
  optimalSpeedHi: 0.75,
  fuelMax: 110,
  hullMax: 130,
  shieldMax: 90,
  energyMax: 110,
  fuelBurnPerSec: 1.0,
  energyRegenPerSec: 12,
  shieldRegenPerSec: 3.5,
});
export const SHIP_SKIFF: ShipSpec = makeSkiff();

export const SKIFF_HARDPOINTS: Hardpoint[] = [
  makeHardpoint('hp-dorsal-1', 'HP_dorsal_1', 'weapon', 'medium', 'gimbal', 'attack', 40, [0, 0.52, 1.45]),
  makeHardpoint('hp-ventral-1', 'HP_ventral_1', 'weapon', 'medium', 'fixed', 'attack', 40, [0, -0.42, 1.15], [0, 0, 1], [0, -1, 0]),
  makeHardpoint('hp-wing-l', 'HP_wing_l', 'weapon', 'small', 'fixed', 'attack', 24, [-0.98, 0.08, 0.78]),
  makeHardpoint('hp-wing-r', 'HP_wing_r', 'weapon', 'small', 'fixed', 'attack', 24, [0.98, 0.08, 0.78]),
  makeHardpoint('hp-core-power', 'HP_core_power', 'component', 'medium', 'internal', 'systems', 0, [0, 0.1, -0.25]),
  makeHardpoint('hp-core-shield', 'HP_core_shield', 'component', 'small', 'internal', 'defense', 0, [0, 0.16, 0.18]),
  makeHardpoint('hp-core-thruster', 'HP_core_thruster', 'component', 'medium', 'internal', 'mobility', 0, [0, -0.08, -0.72]),
  makeHardpoint('hp-util-1', 'HP_util_1', 'component', 'small', 'internal', 'systems', 0, [0.46, 0.1, 0.06]),
];

const makeDefaultSkiffLoadout = (): Loadout => ({
  hardpoints: SKIFF_HARDPOINTS,
  fitted: {
    'hp-dorsal-1': 'railgun-m2', // Closest starter match to the spec's missing mod-coilgun-mk1.
    'hp-wing-l': 'pulse-laser-s1', // Closest starter match to the spec's missing mod-pulse-s.
    'hp-wing-r': 'pulse-laser-s1', // Closest starter match to the spec's missing mod-pulse-s.
    'hp-core-power': 'reactor-overclock-m2', // Closest starter match to the spec's missing mod-powerplant-light.
    'hp-core-shield': 'shield-booster-s1', // Closest starter match to the spec's missing mod-shield-light.
    'hp-core-thruster': 'thruster-tuning-m2', // Closest starter match to the spec's missing mod-thruster-sport.
  },
});
export const DEFAULT_SKIFF_LOADOUT: Loadout = makeDefaultSkiffLoadout();

/** Nimble light fighter — low mass, high thrust, fast turn, twitchy. */
const makeFighter = (): ShipSpec => ({
  id: 'fighter',
  name: 'Wasp Interceptor',
  className: 'light',
  massKg: 18_000,
  inertia: { ix: 2.2e5, iy: 2.2e5, iz: 1.1e5 },
  thrust: 160_000,
  linearDamping: 0.5,
  maxSpeedKmS: 18,
  mouseSens: 0.0045,
  keyTurnRate: 2.4,
  rollRate: 2.8,
  angularDamping: 9.0,
  pitchYawAccel: 9.0,
  rollAccel: 11.0,
  maxAngAccel: 6.0,
  optimalSpeedLo: 0.45,
  optimalSpeedHi: 0.8,
  fuelMax: 80,
  hullMax: 100,
  shieldMax: 80,
  energyMax: 120,
  fuelBurnPerSec: 1.2,
  energyRegenPerSec: 14,
  shieldRegenPerSec: 4,
});
export const SHIP_FIGHTER: ShipSpec = makeFighter();

/** Mid hauler — balanced workhorse; moderate everything. */
const makeHauler = (): ShipSpec => ({
  id: 'hauler',
  name: 'Mule Freighter',
  className: 'medium',
  massKg: 50_000,
  inertia: { ix: 8e5, iy: 8e5, iz: 4e5 },
  thrust: 300_000,
  linearDamping: 0.4,
  maxSpeedKmS: 15,
  mouseSens: 0.0035,
  keyTurnRate: 1.6,
  rollRate: 1.8,
  angularDamping: 8.0,
  pitchYawAccel: 5.5,
  rollAccel: 6.5,
  maxAngAccel: 3.0,
  optimalSpeedLo: 0.4,
  optimalSpeedHi: 0.7,
  fuelMax: 160,
  hullMax: 180,
  shieldMax: 120,
  energyMax: 100,
  fuelBurnPerSec: 0.8,
  energyRegenPerSec: 10,
  shieldRegenPerSec: 3,
});
export const SHIP_HAULER: ShipSpec = makeHauler();

/** Heavy — sluggish, massive, slow to turn but tanky. */
const makeHeavy = (): ShipSpec => ({
  id: 'heavy',
  name: 'Bastion Cruiser',
  className: 'heavy',
  massKg: 140_000,
  inertia: { ix: 3.0e6, iy: 3.0e6, iz: 1.6e6 },
  thrust: 620_000,
  linearDamping: 0.36,
  maxSpeedKmS: 12,
  mouseSens: 0.0024,
  keyTurnRate: 0.9,
  rollRate: 1.0,
  angularDamping: 6.0,
  pitchYawAccel: 3.0,
  rollAccel: 3.5,
  maxAngAccel: 1.4,
  optimalSpeedLo: 0.35,
  optimalSpeedHi: 0.6,
  fuelMax: 320,
  hullMax: 360,
  shieldMax: 220,
  energyMax: 90,
  fuelBurnPerSec: 0.5,
  energyRegenPerSec: 8,
  shieldRegenPerSec: 2,
});
export const SHIP_HEAVY: ShipSpec = makeHeavy();

export const SHIP_TYPES: ShipSpec[] = [SHIP_SKIFF, SHIP_FIGHTER, SHIP_HAULER, SHIP_HEAVY];

// --- SHIP-EXPANSION (save v5): Vesper-3 — the player's starting hull. A LIGHT
// scout-courier (Guild of the Cenotaph): nimble, lightly armed, slightly faster
// and more fragile than the Pallbearer skiff. Hand-tuned (NOT archetype-derived)
// so it flies as a deliberate courier, not a scaled baseline. It stays in
// STORY_SHIP_IDS (so isAmbientSpawnable still gates it OUT of ambient pools) and
// is the single source of truth for the id everywhere (EXPANSION_SHIP_TYPES below
// substitutes this spec in place of the archetype-derived one). ---
const makeVesper3 = (): ShipSpec => ({
  id: 'vesper3',
  name: 'Vesper-3',
  className: 'light',
  faction: 'guild',
  massKg: 26_000,
  inertia: { ix: 3.0e5, iy: 3.0e5, iz: 1.5e5 },
  thrust: 215_000,
  linearDamping: 0.44,
  maxSpeedKmS: 18, // slightly faster than the skiff (16)
  mouseSens: 0.0044,
  keyTurnRate: 2.3, // nimble: between skiff (2.0) and fighter (2.4)
  rollRate: 2.7,
  angularDamping: 9.0,
  pitchYawAccel: 8.0,
  rollAccel: 9.5,
  maxAngAccel: 5.2,
  optimalSpeedLo: 0.45,
  optimalSpeedHi: 0.8,
  fuelMax: 120, // courier range
  hullMax: 105, // more fragile than the skiff (130)
  shieldMax: 78, // more fragile than the skiff (90)
  energyMax: 115,
  fuelBurnPerSec: 1.0,
  energyRegenPerSec: 13,
  shieldRegenPerSec: 3.5,
});
export const SHIP_VESPER3: ShipSpec = makeVesper3();

/** Player-selectable roster surface for the client ships/outfitting page: the 4
 * unchanged starter hulls + the player-flyable Vesper-3. Distinct from SHIP_TYPES
 * (the starter 4, kept byte-stable) and from the ambient pools (Vesper-3 stays
 * story-gated via STORY_SHIP_IDS). */
export const PLAYER_SELECTABLE_SHIP_TYPES: ShipSpec[] = [...SHIP_TYPES, SHIP_VESPER3];

// --- Wave 4: hostile fleet. NON-player ships flown only by encounter NPCs.
// Kept OUT of SHIP_TYPES (the player-selectable set) but resolvable via shipById
// so npcCombat's makeCombatBody/driveFromSpec read their distinct handling. ---

/** Raider — fast, fragile, aggressive. Out-turns everything; folds under fire. */
const makeRaider = (): ShipSpec => ({
  id: 'raider',
  name: 'Hookblade Raider',
  className: 'light',
  faction: 'belt',
  massKg: 14_000,
  inertia: { ix: 1.7e5, iy: 1.7e5, iz: 0.85e5 },
  thrust: 155_000,
  linearDamping: 0.52,
  maxSpeedKmS: 21,
  mouseSens: 0.005,
  keyTurnRate: 2.8,
  rollRate: 3.2,
  angularDamping: 9.5,
  pitchYawAccel: 10.5,
  rollAccel: 12.5,
  maxAngAccel: 7.0,
  optimalSpeedLo: 0.48,
  optimalSpeedHi: 0.85,
  fuelMax: 70,
  hullMax: 70,
  shieldMax: 55,
  energyMax: 120,
  fuelBurnPerSec: 1.4,
  energyRegenPerSec: 14,
  shieldRegenPerSec: 3.5,
});
export const SHIP_RAIDER: ShipSpec = makeRaider();

/** Interdictor — heavy, armored, slow. A wall: huge hull, glacial turn. */
const makeInterdictor = (): ShipSpec => ({
  id: 'interdictor',
  name: 'Mauler Interdictor',
  className: 'heavy',
  faction: 'belt',
  massKg: 170_000,
  inertia: { ix: 3.4e6, iy: 3.4e6, iz: 1.8e6 },
  thrust: 660_000,
  linearDamping: 0.34,
  maxSpeedKmS: 11,
  mouseSens: 0.0022,
  keyTurnRate: 0.8,
  rollRate: 0.9,
  angularDamping: 5.5,
  pitchYawAccel: 2.6,
  rollAccel: 3.0,
  maxAngAccel: 1.2,
  optimalSpeedLo: 0.32,
  optimalSpeedHi: 0.58,
  fuelMax: 340,
  hullMax: 440,
  shieldMax: 300,
  energyMax: 90,
  fuelBurnPerSec: 0.5,
  energyRegenPerSec: 8,
  shieldRegenPerSec: 2,
});
export const SHIP_INTERDICTOR: ShipSpec = makeInterdictor();

/** Patrol — balanced, lean. The all-rounder hostile; moderate everything. */
const makePatrol = (): ShipSpec => ({
  id: 'patrol',
  name: 'Lance Patrol',
  className: 'medium',
  faction: 'concord',
  massKg: 38_000,
  inertia: { ix: 6.0e5, iy: 6.0e5, iz: 3.0e5 },
  thrust: 280_000,
  linearDamping: 0.44,
  maxSpeedKmS: 16,
  mouseSens: 0.0036,
  keyTurnRate: 1.8,
  rollRate: 2.0,
  angularDamping: 8.2,
  pitchYawAccel: 6.2,
  rollAccel: 7.2,
  maxAngAccel: 3.4,
  optimalSpeedLo: 0.42,
  optimalSpeedHi: 0.72,
  fuelMax: 130,
  hullMax: 150,
  shieldMax: 110,
  energyMax: 100,
  fuelBurnPerSec: 0.9,
  energyRegenPerSec: 11,
  shieldRegenPerSec: 3,
});
export const SHIP_PATROL: ShipSpec = makePatrol();

/** Salvager — bulky, slow, weak. Non-combatant hull: lots of mass, thin defenses. */
const makeSalvager = (): ShipSpec => ({
  id: 'salvager',
  name: 'Gravedigger Salvager',
  className: 'heavy',
  faction: 'belt',
  massKg: 95_000,
  inertia: { ix: 1.7e6, iy: 1.7e6, iz: 0.95e6 },
  thrust: 360_000,
  linearDamping: 0.4,
  maxSpeedKmS: 10,
  mouseSens: 0.0026,
  keyTurnRate: 1.1,
  rollRate: 1.2,
  angularDamping: 6.5,
  pitchYawAccel: 3.2,
  rollAccel: 3.8,
  maxAngAccel: 1.6,
  optimalSpeedLo: 0.3,
  optimalSpeedHi: 0.55,
  fuelMax: 200,
  hullMax: 120,
  shieldMax: 60,
  energyMax: 80,
  fuelBurnPerSec: 0.7,
  energyRegenPerSec: 7,
  shieldRegenPerSec: 1.5,
});
export const SHIP_SALVAGER: ShipSpec = makeSalvager();

/** Wave 4 hostile roster — flown by encounter NPCs, never player-selectable. */
export const ENEMY_SHIP_TYPES: ShipSpec[] = [
  SHIP_RAIDER,
  SHIP_INTERDICTOR,
  SHIP_PATROL,
  SHIP_SALVAGER,
];

// --- SHIP-EXPANSION: the full faction roster (ship-expansion.md). 27 spec-only
// hulls authored as DATA + scaled from per-class archetype baselines by mass, so
// the whole roster shares the player fleet's handling feel without bespoke tuning.
// These are flown by encounter NPCs / traffic / campaign beats only — NEVER added
// to the player-selectable SHIP_TYPES, and they carry NO hardpoints (art deferred). ---

/** Per-class archetype baseline: the reference mass + handling at that mass. An
 * expansion hull scales from its class's baseline — inertia + thrust + capacities
 * grow with mass; turn rates ease off as mass climbs above the reference. */
interface ArchetypeBaseline {
  refMassKg: number;
  iSidePerKg: number;
  iRollPerKg: number;
  thrustPerKg: number;
  linearDamping: number;
  maxSpeedKmS: number;
  mouseSens: number;
  keyTurnRate: number;
  rollRate: number;
  angularDamping: number;
  pitchYawAccel: number;
  rollAccel: number;
  maxAngAccel: number;
  optimalSpeedLo: number;
  optimalSpeedHi: number;
  fuelMax: number;
  hullMax: number;
  shieldMax: number;
  energyMax: number;
  fuelBurnPerSec: number;
  energyRegenPerSec: number;
  shieldRegenPerSec: number;
}

const ARCHETYPES: Record<ShipSpec['className'], ArchetypeBaseline> = {
  light: {
    refMassKg: 18_000,
    iSidePerKg: 12.2,
    iRollPerKg: 6.1,
    thrustPerKg: 8.9,
    linearDamping: 0.5,
    maxSpeedKmS: 18,
    mouseSens: 0.0045,
    keyTurnRate: 2.4,
    rollRate: 2.8,
    angularDamping: 9.0,
    pitchYawAccel: 9.0,
    rollAccel: 11.0,
    maxAngAccel: 6.0,
    optimalSpeedLo: 0.45,
    optimalSpeedHi: 0.8,
    fuelMax: 80,
    hullMax: 100,
    shieldMax: 80,
    energyMax: 120,
    fuelBurnPerSec: 1.2,
    energyRegenPerSec: 14,
    shieldRegenPerSec: 4,
  },
  medium: {
    refMassKg: 45_000,
    iSidePerKg: 16,
    iRollPerKg: 8,
    thrustPerKg: 7.4,
    linearDamping: 0.44,
    maxSpeedKmS: 16,
    mouseSens: 0.0036,
    keyTurnRate: 1.8,
    rollRate: 2.0,
    angularDamping: 8.2,
    pitchYawAccel: 6.2,
    rollAccel: 7.2,
    maxAngAccel: 3.4,
    optimalSpeedLo: 0.42,
    optimalSpeedHi: 0.72,
    fuelMax: 130,
    hullMax: 150,
    shieldMax: 110,
    energyMax: 100,
    fuelBurnPerSec: 0.9,
    energyRegenPerSec: 11,
    shieldRegenPerSec: 3,
  },
  heavy: {
    refMassKg: 140_000,
    iSidePerKg: 21.4,
    iRollPerKg: 11.4,
    thrustPerKg: 4.43,
    linearDamping: 0.36,
    maxSpeedKmS: 12,
    mouseSens: 0.0024,
    keyTurnRate: 0.9,
    rollRate: 1.0,
    angularDamping: 6.0,
    pitchYawAccel: 3.0,
    rollAccel: 3.5,
    maxAngAccel: 1.4,
    optimalSpeedLo: 0.35,
    optimalSpeedHi: 0.6,
    fuelMax: 320,
    hullMax: 360,
    shieldMax: 220,
    energyMax: 90,
    fuelBurnPerSec: 0.5,
    energyRegenPerSec: 8,
    shieldRegenPerSec: 2,
  },
};

interface ExpansionInput {
  id: string;
  name: string;
  className: ShipSpec['className'];
  massKg: number;
  faction: FactionId;
}

const round1 = (v: number): number => Math.round(v * 10) / 10;

/**
 * Archetype helper: derive a full ShipSpec for an expansion hull from its class
 * baseline + mass. Inertia/thrust/capacities scale with mass (terminal speed stays
 * class-constant since thrust ∝ mass); turn rates scale by sqrt(refMass/mass) so a
 * heavier-than-reference hull turns more sluggishly and a lighter one snappier.
 * Pure + deterministic (no randomness, no I/O).
 */
export const makeExpansionSpec = (input: ExpansionInput): ShipSpec => {
  const b = ARCHETYPES[input.className];
  const capScale = input.massKg / b.refMassKg; // capacities/inertia grow with mass
  const turnScale = Math.sqrt(b.refMassKg / input.massKg); // agility eases off with mass
  return {
    id: input.id,
    name: input.name,
    className: input.className,
    faction: input.faction,
    massKg: input.massKg,
    inertia: {
      ix: b.iSidePerKg * input.massKg,
      iy: b.iSidePerKg * input.massKg,
      iz: b.iRollPerKg * input.massKg,
    },
    thrust: b.thrustPerKg * input.massKg,
    linearDamping: b.linearDamping,
    maxSpeedKmS: b.maxSpeedKmS,
    mouseSens: b.mouseSens,
    keyTurnRate: round1(b.keyTurnRate * turnScale),
    rollRate: round1(b.rollRate * turnScale),
    angularDamping: b.angularDamping,
    pitchYawAccel: round1(b.pitchYawAccel * turnScale),
    rollAccel: round1(b.rollAccel * turnScale),
    maxAngAccel: round1(b.maxAngAccel * turnScale),
    optimalSpeedLo: b.optimalSpeedLo,
    optimalSpeedHi: b.optimalSpeedHi,
    fuelMax: Math.round(b.fuelMax * capScale),
    hullMax: Math.round(b.hullMax * capScale),
    shieldMax: Math.round(b.shieldMax * capScale),
    energyMax: b.energyMax,
    fuelBurnPerSec: b.fuelBurnPerSec,
    energyRegenPerSec: b.energyRegenPerSec,
    shieldRegenPerSec: b.shieldRegenPerSec,
  };
};

/** The 27 spec-only expansion hulls (mass/class/faction from ship-expansion.md). */
const EXPANSION_INPUTS: ExpansionInput[] = [
  // Faction 1 — Guild of the Cenotaph
  { id: 'cortege', name: 'Cortège Runner', className: 'medium', massKg: 35_000, faction: 'guild' },
  { id: 'dirge', name: 'Dirge Heavy Tender', className: 'heavy', massKg: 110_000, faction: 'guild' },
  { id: 'vesper3', name: 'Vesper-3', className: 'light', massKg: 26_000, faction: 'guild' },
  // Faction 2 — Concord of Sol
  { id: 'vigil', name: 'Vigil-class Destroyer', className: 'heavy', massKg: 180_000, faction: 'concord' },
  { id: 'sentinel', name: 'Sentinel Cutter', className: 'light', massKg: 22_000, faction: 'concord' },
  { id: 'warden', name: 'Warden Frigate', className: 'medium', massKg: 65_000, faction: 'concord' },
  { id: 'hellebore', name: 'Hellebore', className: 'light', massKg: 24_000, faction: 'concord' },
  // Faction 3 — Aresian Republic
  { id: 'phobos', name: 'Phobos-class Corvette', className: 'medium', massKg: 48_000, faction: 'aresian' },
  { id: 'deimos', name: 'Deimos Gunship', className: 'heavy', massKg: 130_000, faction: 'aresian' },
  { id: 'dustrunner', name: 'Dust Runner', className: 'light', massKg: 15_000, faction: 'aresian' },
  // Faction 4 — Spún Compact (Belt)
  { id: 'longmule', name: 'Long Mule', className: 'medium', massKg: 55_000, faction: 'belt' },
  { id: 'thermal', name: 'Thermal', className: 'medium', massKg: 52_000, faction: 'belt' },
  { id: 'cleaver', name: 'Cleaver Striker', className: 'light', massKg: 12_000, faction: 'belt' },
  { id: 'cairn', name: 'Cairn Carrier', className: 'heavy', massKg: 2_000_000, faction: 'belt' },
  // Faction 5 — Jovian Concession
  { id: 'lictor', name: 'Lictor-class Frigate', className: 'medium', massKg: 58_000, faction: 'jovian' },
  { id: 'proctor', name: 'Proctor Corvette', className: 'light', massKg: 25_000, faction: 'jovian' },
  { id: 'prefect', name: 'Prefect Cruiser', className: 'heavy', massKg: 160_000, faction: 'jovian' },
  // Faction 6 — Outer Concordance
  { id: 'listener', name: "Listener's Hauler", className: 'medium', massKg: 40_000, faction: 'outer' },
  { id: 'censer', name: 'Censer Survey Vessel', className: 'light', massKg: 20_000, faction: 'outer' },
  { id: 'matins', name: 'Matins Cutter', className: 'medium', massKg: 42_000, faction: 'outer' },
  // The Quiet (conditional — gated OUT of ambient pools)
  { id: 'whisper', name: 'Whisper Drone', className: 'light', massKg: 8_000, faction: 'quiet' },
  { id: 'mourner', name: 'Mourner', className: 'medium', massKg: 40_000, faction: 'quiet' },
  { id: 'echo', name: 'Cenotaph Echo', className: 'heavy', massKg: 5_000_000, faction: 'quiet' },
  { id: 'requiem', name: 'Requiem', className: 'light', massKg: 3_000, faction: 'quiet' },
  // Bosses & unique ships (gated OUT of ambient pools)
  { id: 'augustin', name: "Augustín's Flagship", className: 'heavy', massKg: 85_000, faction: 'concord' },
  { id: 'redveil', name: 'Red Veil', className: 'heavy', massKg: 140_000, faction: 'aresian' },
  { id: 'lightmule', name: 'Light Mule', className: 'light', massKg: 20_000, faction: 'independent' },
];

/** SHIP-EXPANSION roster — resolvable via shipById. Every hull is archetype-
 * derived EXCEPT Vesper-3, which uses its hand-tuned SHIP_VESPER3 spec (the id
 * stays registered here so the roster count + faction partition are unchanged;
 * it is additionally player-selectable via PLAYER_SELECTABLE_SHIP_TYPES). */
export const EXPANSION_SHIP_TYPES: ShipSpec[] = EXPANSION_INPUTS.map((input) =>
  input.id === SHIP_VESPER3.id ? SHIP_VESPER3 : makeExpansionSpec(input),
);

/** The Quiet — alien hulls that NEVER appear in ambient encounter/traffic pools. */
export const QUIET_SHIP_IDS: readonly string[] = ['whisper', 'mourner', 'echo', 'requiem'];
/** Boss / capital hulls reserved for scripted encounters — never ambient. */
export const BOSS_SHIP_IDS: readonly string[] = ['augustin', 'redveil', 'cairn'];
/** Unique named story hulls — wired only at scripted campaign spawn points. */
export const STORY_SHIP_IDS: readonly string[] = ['vesper3', 'hellebore', 'thermal'];
/** Hulls with no offensive weapon mount — excluded from hostile (enemy) pools. */
const NON_COMBATANT_IDS: readonly string[] = ['listener'];

const PLAYER_SHIP_IDS = new Set(SHIP_TYPES.map((s) => s.id));
const NON_AMBIENT_IDS = new Set<string>([...QUIET_SHIP_IDS, ...BOSS_SHIP_IDS, ...STORY_SHIP_IDS]);
/** Player faction map for the 4 unchanged player hulls (kept off their consts). */
const PLAYER_FACTION: Record<string, FactionId> = {
  skiff: 'guild',
  fighter: 'guild',
  hauler: 'guild',
  heavy: 'guild',
};

/** Player fleet + hostile fleet + expansion roster, for id resolution (NOT selection). */
export const ALL_SHIP_TYPES: ShipSpec[] = [
  ...SHIP_TYPES,
  ...ENEMY_SHIP_TYPES,
  ...EXPANSION_SHIP_TYPES,
];

export const shipById = (id: string): ShipSpec =>
  ALL_SHIP_TYPES.find((s) => s.id === id) ?? SHIP_HAULER;

/** Resolve a hull's faction (player ships → 'guild'; unknown → 'independent'). */
export const factionOf = (id: string): FactionId =>
  ALL_SHIP_TYPES.find((s) => s.id === id)?.faction ?? PLAYER_FACTION[id] ?? 'independent';

/** Every hull belonging to a faction (across player + enemy + expansion sets). */
export const shipsByFaction = (faction: FactionId): ShipSpec[] =>
  ALL_SHIP_TYPES.filter((s) => factionOf(s.id) === faction);

/** True when a hull may appear in ambient encounter/traffic pools (excludes the
 * Quiet, bosses, and unique story ships — all reserved for scripted spawns). */
export const isAmbientSpawnable = (id: string): boolean => !NON_AMBIENT_IDS.has(id);

/** Factions that own ambient territory (everything but the Quiet, which is never ambient). */
export const AMBIENT_FACTIONS: readonly FactionId[] = [
  'guild',
  'concord',
  'aresian',
  'belt',
  'jovian',
  'outer',
  'independent',
];

/**
 * Deterministic hostile ship-type pool for a faction — combat-capable, ambient
 * hulls of that faction, excluding player ships and weaponless hulls. Falls back
 * to the legacy 4-ship enemy roster if a faction has no eligible hulls, so the
 * pool is never empty. The Quiet + bosses can never appear (isAmbientSpawnable).
 */
export const ambientEnemyTypesForFaction = (faction: FactionId): string[] => {
  const pool = ALL_SHIP_TYPES.filter(
    (s) =>
      factionOf(s.id) === faction &&
      isAmbientSpawnable(s.id) &&
      !PLAYER_SHIP_IDS.has(s.id) &&
      !NON_COMBATANT_IDS.includes(s.id),
  ).map((s) => s.id);
  return pool.length > 0 ? pool : ENEMY_SHIP_TYPES.map((s) => s.id);
};

/**
 * Deterministic civilian traffic pool for a faction — ambient hulls of that
 * faction (player civilian hulls included). Falls back to the legacy lane fleet
 * if empty. The Quiet + bosses can never appear (isAmbientSpawnable).
 */
export const ambientTrafficTypesForFaction = (faction: FactionId): string[] => {
  const pool = ALL_SHIP_TYPES.filter(
    (s) => factionOf(s.id) === faction && isAmbientSpawnable(s.id),
  ).map((s) => s.id);
  return pool.length > 0 ? pool : ['fighter', 'hauler', 'heavy'];
};
